<template>
  <header class="app-header">
    <div class="header-left">
      <!-- Hamburger: drawer toggle on mobile/tablet, sidebar collapse on desktop -->
      <button
        v-if="!ui.isDesktop"
        class="header-menu-btn"
        @click="ui.toggleMobileNav()"
        :title="ui.mobileNavOpen ? 'Close menu' : 'Open menu'"
      >
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>
      <button
        v-else
        class="header-menu-btn"
        @click="ui.toggleSidebar()"
        :title="ui.sidebarOpen ? 'Close sidebar' : 'Open sidebar'"
      >
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>

      <router-link to="/" class="header-brand" v-if="!ui.isDesktop && !ui.mobileNavOpen">
        <Logo :size="28" />
        <h1 class="header-title">Wiki</h1>
      </router-link>
    </div>

    <div class="header-center">
      <div class="search-container">
        <el-autocomplete
          ref="searchInput"
          v-model="searchQuery"
          placeholder="Search... (press /)"
          :fetch-suggestions="fetchSuggestions"
          :trigger-on-focus="false"
          @select="selectSuggestion"
          @keyup.enter="handleSearch"
        >
          <template #suffix>
            <el-button
              type="primary"
              :icon="Search"
              @click="handleSearch"
            />
          </template>
          <template #default="{ item }">
            <div>{{ item?.value }}</div>
          </template>
        </el-autocomplete>
      </div>
    </div>

    <div class="header-right">
      <!-- Page actions on wiki pages (desktop only — mobile uses bottom nav) -->
      <template v-if="isWikiPage && ui.isDesktop">
        <template v-if="isEditing">
          <button @click="pageEditActions.save?.()" class="header-action-btn" title="Save changes">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            <span class="action-label">Save</span>
          </button>
          <button @click="pageEditActions.cancel?.()" class="header-action-btn" title="Cancel editing">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            <span class="action-label">Cancel</span>
          </button>
        </template>
        <template v-else>
          <button v-if="pageEditActions.canEdit" @click="editPage" class="header-action-btn" title="Edit page">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            <span class="action-label">Edit</span>
          </button>
          <button @click="viewHistory" class="header-action-btn" title="View history">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span class="action-label">History</span>
          </button>
          <button
            v-if="pageEditActions.aiImprove && pageEditActions.canEdit"
            class="header-action-btn"
            title="Ask AI to improve this page"
            @click="pageEditActions.aiImprove?.()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"></path>
              <path d="M19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9z"></path>
            </svg>
            <span class="action-label">AI</span>
          </button>
          <button v-if="pageEditActions.canEdit" @click="handleDelete" class="header-action-btn header-action-btn--danger" title="Delete page">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            <span class="action-label">Delete</span>
          </button>
        </template>
      </template>

      <div v-if="authStore.isAuthenticated" ref="userMenuRef" class="user-menu" @click.stop="showMenu = !showMenu">
        <span class="username">{{ authStore.username }}</span>
        <div v-if="showMenu" class="menu-dropdown">
          <router-link v-if="authStore.isAdmin" to="/admin" class="menu-item">Administration</router-link>
          <router-link to="/profile" class="menu-item">Profile</router-link>
          <button @click="logout" class="menu-item">Logout</button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useUiStore } from '../stores/ui'
import { Search } from '@element-plus/icons-vue'
import { ElMessageBox } from 'element-plus'
import axios from 'axios'
import { ElAutocomplete } from 'element-plus'
import { debounce } from 'lodash-es'

import { authFetch } from '../utils/api'
import Logo from './Logo.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const isWikiPage = computed(() => route.path.startsWith('/wiki/'))
const isEditing = computed(() => route.query.edit === 'true')

const pageEditActions = inject<{ save?: () => Promise<void>; cancel?: () => void; aiImprove?: () => void; canEdit?: boolean }>('pageEditActions')!

const editPage = () => {
  router.push({ path: route.path, query: { edit: 'true' } })
}

const viewHistory = () => {
  router.push(route.path.replace('/wiki', '/history'))
}

const handleDelete = async () => {
  try {
    await ElMessageBox.confirm(
      'Are you sure you want to delete this page? This cannot be undone.',
      'Delete Page',
      {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }
    )
  } catch {
    return // user cancelled
  }

  try {
    const path = route.params.pathMatch as string
    const res = await authFetch(`/api/pages/${encodeURIComponent(path)}`, {
      method: 'DELETE',
    })
    if (!res.ok) {
      ElMessageBox.alert('Failed to delete page', 'Error')
      return
    }
    router.push('/')
  } catch {
    ElMessageBox.alert('An error occurred while deleting the page', 'Error')
  }
}
const ui = useUiStore()
const showMenu = ref(false)

const searchQuery = ref('')
const searchResults = ref<string[]>([])
const loading = ref(false)

const fetchSuggestions = (query: string, cb: (results: Array<{ value: string }>) => void) => {
  if (!query.trim()) {
    searchResults.value = []
    cb([])
    return
  }

  loading.value = true
  axios.get('/api/search/suggest', {
    params: { q: query }
  })
    .then(response => {
      searchResults.value = response.data.results
      cb(response.data.results?.map(r => ({ value: r })) || [])
    })
    .catch(() => {
      searchResults.value = []
      cb([])
    })
    .finally(() => {
      loading.value = false
    })
}

const debouncedFetch = debounce((query: string) => {
  fetchSuggestions(query, (results) => {
    searchResults.value = results.map(r => r.value)
  })
}, 300)

watch(searchQuery, (newVal) => {
  debouncedFetch(newVal)
})

const handleSearch = () => {
  const q = searchQuery.value.trim().replace(/^\//, '')
  if (q) {
    router.push({
      path: searchResults.value.includes(searchQuery.value)
        ? `/wiki/${encodeURIComponent(q)}`.replaceAll('%2F', '/')
        : `/wiki/${encodeURIComponent(q)}?edit=true`.replaceAll('%2F', '/')
    })
    searchQuery.value = ''
    showMenu.value = false
  }
}

const selectSuggestion = (item: Record<string, any>) => {
  if (!item?.value) return
  searchQuery.value = item.value
  handleSearch()
}

const logout = async () => {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
  } catch {
    // proceed
  }
  authStore.logout()
  router.push('/login')
}

const searchInput = ref<InstanceType<typeof ElAutocomplete>>()
const userMenuRef = ref<HTMLElement>()

const hideMenu = (e: MouseEvent) => {
  if (userMenuRef.value && !userMenuRef.value.contains(e.target as Node)) {
    showMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('keydown', (e) => {
    if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
      e.preventDefault()
      searchInput.value?.focus()
    }
  })
  document.addEventListener('click', hideMenu)
})

onUnmounted(() => {
  document.removeEventListener('click', hideMenu)
})
</script>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  height: var(--header-height);
  padding: 0 var(--space-md);
  background-color: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  gap: var(--space-md);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  flex-shrink: 0;
}

.header-menu-btn {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-xs);
  border-radius: var(--radius-sm);
  transition: background var(--transition-fast);
}

.header-menu-btn:hover {
  background: var(--color-surface-hover);
}

.hamburger-line {
  display: block;
  width: 18px;
  height: 2px;
  background-color: var(--color-text-secondary);
  border-radius: 1px;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  text-decoration: none;
  color: var(--color-text);
}

.header-title {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
  transform: skewX(-5deg);
  white-space: nowrap;
}

.header-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.search-container {
  width: 100%;
  max-width: 480px;
}

.search-container :deep(.el-autocomplete) {
  width: 100%;
}

.search-container :deep(.el-input__wrapper) {
  border-radius: 4px 0 0 4px;
  margin-right: -1px;
  padding-right: 0;
}

:deep(.el-input__inner) {
  width: 100% !important;
}

.search-container :deep(.el-button) {
  border-radius: 0 4px 4px 0;
  height: 100%;
  margin-left: 0;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-right {
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  padding: 4px 8px;
  cursor: pointer;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-family: inherit;
  white-space: nowrap;
  transition: background var(--transition-fast), color var(--transition-fast), border-color var(--transition-fast);
}

.header-action-btn:hover {
  background: var(--color-surface-hover);
  color: var(--color-text);
  border-color: var(--color-border);
}

.header-action-btn svg {
  flex-shrink: 0;
}

.header-action-btn--danger {
  color: var(--color-danger);
}

.header-action-btn--danger:hover {
  color: #fff;
  background-color: var(--color-danger);
  border-color: var(--color-danger);
}

@media (max-width: 640px) {
  .action-label {
    display: none;
  }
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--radius-sm);
  transition: background var(--transition-fast);
}

.user-menu:hover {
  background-color: var(--color-surface-hover);
}

.username {
  font-weight: 500;
  font-size: var(--font-size-sm);
}

.menu-dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  min-width: 120px;
  overflow: hidden;
  z-index: 200;
}

.menu-item {
  width: 100%;
  padding: 0.5rem 1rem;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text);
  font-size: var(--font-size-sm);
  transition: background var(--transition-fast);
}

.menu-item:hover {
  background-color: var(--color-surface-hover);
}

/* Adjustments for non-desktop (mobile + tablet) */
@media (max-width: 1023px) {
  .app-header {
    padding: 0 var(--space-sm);
    gap: var(--space-sm);
  }

  .header-center {
    flex: 1;
  }
}

/* Tight adjustments for mobile */
@media (max-width: 639px) {
  .header-title {
    display: none;
  }
}
</style>
